import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Loader2, CheckCircle, XCircle } from "lucide-react";
import { apiClient } from "../services/api";

const inputWrap = {
  position: "relative",
  display: "flex",
  alignItems: "center",
};

const inputStyle = {
  backgroundColor: "var(--lm-s1)",
  border: "1px solid var(--lm-border)",
  color: "var(--lm-ink)",
  borderRadius: "8px",
  padding: "10px 40px 10px 12px",
  fontSize: "14px",
  width: "100%",
  outline: "none",
  transition: "border-color 0.15s",
  boxSizing: "border-box",
};

const strengthColor = (s) =>
  s >= 4 ? "var(--lm-success)" : s >= 3 ? "#f59e0b" : "var(--lm-error)";

const strengthLabel = (s) =>
  s >= 4 ? "Fort" : s >= 3 ? "Moyen" : s >= 1 ? "Faible" : "";

const getStrength = (pwd) => {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[a-z]/.test(pwd)) score++;
  if (/\d/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return score;
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) navigate("/forgot-password", { replace: true });
  }, [token, navigate]);

  const strength = getStrength(password);
  const mismatch = confirm && password !== confirm;
  const canSubmit = strength >= 4 && password === confirm && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      await apiClient.post("v1/auth/reset-password", {
        token,
        new_password: password,
      });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(msg || "Lien invalide ou expiré. Recommencez depuis la page mot de passe oublié.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: "var(--lm-canvas)" }}
    >
      <div style={{ width: "100%", maxWidth: "400px" }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <span style={{ fontSize: "20px", fontWeight: 700, color: "var(--lm-ink)", letterSpacing: "-0.4px" }}>
            <span style={{ color: "#5e6ad2" }}>m</span>awered
          </span>
        </div>

        {/* Card */}
        <div style={{
          backgroundColor: "var(--lm-s1)",
          border: "1px solid var(--lm-border)",
          borderRadius: "12px",
          padding: "36px",
        }}>
          {done ? (
            /* Success */
            <div style={{ textAlign: "center" }}>
              <div style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 48, height: 48, borderRadius: "12px",
                backgroundColor: "rgba(39,166,68,0.1)", marginBottom: "20px",
              }}>
                <CheckCircle style={{ width: 24, height: 24, color: "var(--lm-success)" }} />
              </div>
              <h1 style={{ margin: "0 0 8px", fontSize: "18px", fontWeight: 600, color: "var(--lm-ink)", letterSpacing: "-0.3px" }}>
                Mot de passe mis à jour
              </h1>
              <p style={{ margin: "0 0 28px", fontSize: "14px", color: "var(--lm-ink-2)", lineHeight: 1.6 }}>
                Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.
              </p>
              <button
                onClick={() => navigate("/login")}
                style={{
                  width: "100%", padding: "10px 16px", borderRadius: "8px",
                  backgroundColor: "#5e6ad2", color: "#fff", fontSize: "14px",
                  fontWeight: 500, border: "none", cursor: "pointer",
                }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = "#828fff")}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
              >
                Se connecter
              </button>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit}>
              <h1 style={{ margin: "0 0 8px", fontSize: "18px", fontWeight: 600, color: "var(--lm-ink)", letterSpacing: "-0.3px" }}>
                Nouveau mot de passe
              </h1>
              <p style={{ margin: "0 0 28px", fontSize: "14px", color: "var(--lm-ink-2)", lineHeight: 1.6 }}>
                Choisissez un mot de passe fort pour sécuriser votre compte.
              </p>

              {/* Password */}
              <div style={{ marginBottom: "16px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "var(--lm-ink-2)", marginBottom: "6px" }}>
                  Nouveau mot de passe
                </label>
                <div style={inputWrap}>
                  <input
                    type={showPwd ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min. 8 caractères"
                    style={inputStyle}
                    onFocus={e => (e.target.style.borderColor = "#5e6ad2")}
                    onBlur={e => (e.target.style.borderColor = "var(--lm-border)")}
                    disabled={loading}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(v => !v)}
                    style={{
                      position: "absolute", right: 10,
                      background: "none", border: "none", cursor: "pointer", padding: 0,
                      color: "var(--lm-ink-3)", display: "flex",
                    }}
                  >
                    {showPwd ? <EyeOff style={{ width: 16, height: 16 }} /> : <Eye style={{ width: 16, height: 16 }} />}
                  </button>
                </div>

                {/* Strength bar */}
                {password && (
                  <div style={{ marginTop: "8px" }}>
                    <div style={{ height: 3, borderRadius: 9999, backgroundColor: "var(--lm-border)", overflow: "hidden" }}>
                      <div style={{
                        height: "100%", borderRadius: 9999,
                        width: `${(strength / 5) * 100}%`,
                        backgroundColor: strengthColor(strength),
                        transition: "width 0.2s, background-color 0.2s",
                      }} />
                    </div>
                    <p style={{ margin: "4px 0 0", fontSize: "12px", color: strengthColor(strength) }}>
                      {strengthLabel(strength)}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm */}
              <div style={{ marginBottom: "20px" }}>
                <label style={{ display: "block", fontSize: "12px", fontWeight: 500, color: "var(--lm-ink-2)", marginBottom: "6px" }}>
                  Confirmer le mot de passe
                </label>
                <div style={inputWrap}>
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Retapez le mot de passe"
                    style={{
                      ...inputStyle,
                      borderColor: mismatch ? "var(--lm-error)" : "var(--lm-border)",
                    }}
                    onFocus={e => (e.target.style.borderColor = mismatch ? "var(--lm-error)" : "#5e6ad2")}
                    onBlur={e => (e.target.style.borderColor = mismatch ? "var(--lm-error)" : "var(--lm-border)")}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(v => !v)}
                    style={{
                      position: "absolute", right: 10,
                      background: "none", border: "none", cursor: "pointer", padding: 0,
                      color: "var(--lm-ink-3)", display: "flex",
                    }}
                  >
                    {showConfirm ? <EyeOff style={{ width: 16, height: 16 }} /> : <Eye style={{ width: 16, height: 16 }} />}
                  </button>
                </div>
                {mismatch && (
                  <p style={{ margin: "4px 0 0", fontSize: "12px", color: "var(--lm-error)", display: "flex", alignItems: "center", gap: 4 }}>
                    <XCircle style={{ width: 12, height: 12 }} /> Les mots de passe ne correspondent pas
                  </p>
                )}
              </div>

              {/* Requirements hint */}
              <div style={{
                backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)",
                borderRadius: "8px", padding: "10px 12px", marginBottom: "20px",
                fontSize: "12px", color: "var(--lm-ink-3)", lineHeight: 1.6,
              }}>
                8 caractères minimum · une majuscule · une minuscule · un chiffre
              </div>

              {error && (
                <div style={{
                  backgroundColor: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)",
                  borderRadius: "8px", padding: "10px 12px", marginBottom: "16px",
                  fontSize: "13px", color: "var(--lm-error)",
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  width: "100%", padding: "10px 16px", borderRadius: "8px",
                  backgroundColor: canSubmit ? "#5e6ad2" : "var(--lm-s2)",
                  color: canSubmit ? "#fff" : "var(--lm-ink-3)",
                  fontSize: "14px", fontWeight: 500,
                  border: `1px solid ${canSubmit ? "transparent" : "var(--lm-border)"}`,
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  transition: "background-color 0.15s",
                }}
                onMouseEnter={e => { if (canSubmit) e.currentTarget.style.backgroundColor = "#828fff"; }}
                onMouseLeave={e => { if (canSubmit) e.currentTarget.style.backgroundColor = "#5e6ad2"; }}
              >
                {loading && <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite" }} />}
                {loading ? "Réinitialisation…" : "Définir le nouveau mot de passe"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
